// Grids & Gantts — a bar on a timeline is a HANDLE, not a picture.
//
// Standard 1.3: WHERE THE WORK IS DRAWN IS WHERE THE WORK IS CHANGED.
//
// A Gantt that can only be read sends the reader somewhere else to change a
// date: open the work item, find the field, pick a day off a calendar, come
// back, check it moved. Meanwhile the thing they wanted to say — "this
// slides a week" — is a gesture, and they already made it with the mouse.
//
// So the bar takes the gesture. Three grips, and which one a press lands on
// is decided by WHERE on the bar it landed:
//
//   its left edge    the start date moves, the due date stays
//   its right edge   the due date moves, the start date stays
//   anywhere else    the whole span slides, keeping its length
//
// A dot — a row with one date only — has no edges worth aiming at, so the
// whole of it slides and the one date it has is the one that moves.
//
// THE MATHS IS HERE AND NOT IN THE RENDERER for two reasons. It is the part
// that can be wrong (a drag that silently inverts a span, a right edge
// dragged past its own start, a pixel offset read at the wrong grain), and
// it is the part every timeline in the fork needs — so it is pure, it is
// shared, and it is tested under node.

import { addDays } from './interval';

// The grab zone at each end of a bar. Eight pixels is about a mouse's own
// accuracy; wider and the middle of a short bar disappears.
export const EDGE = 8;

// A press below this many pixels of travel is a CLICK, not a drag. Without
// it, opening a work item by clicking its bar would nudge its dates by a
// day — at Months a single pixel is a third of a day, and the hand always
// moves a pixel or two.
export const SLOP = 3;

// Which grip a press landed on. A bar too narrow to have a middle is all
// middle: aiming at a 10px bar's "left edge" is not something anybody can
// do on purpose.
export function handleAt(offsetX, barW, { edge = EDGE } = {}) {
  const w = Number(barW) || 0;
  if (w < edge * 3) return 'move';
  const x = Number(offsetX) || 0;
  if (x <= edge) return 'start';
  if (x >= w - edge) return 'end';
  return 'move';
}

// The cursor that says what the grip will do before it is pressed.
export const cursorFor = (handle) => (handle === 'move' ? 'grab' : 'ew-resize');

// Pixels of travel, in days at this grain. Rounded, because a date is a
// whole day and a half-day offset is not a thing a plan can hold.
export const daysFromDx = (dx, px) => (px > 0 ? Math.round((Number(dx) || 0) / px) : 0);

// The dates after the drag, or null when nothing would change.
//
// IT WORKS IN THE BAR THE READER IS LOOKING AT, NOT IN THE TWO FIELDS.
//
// A bar is drawn between the earlier date and the later one — `barOf` sorts
// them — so its left edge is the earlier and its right edge the later,
// whichever FIELD each happens to live in. When the data is inverted (a due
// date before its start, which happens and is flagged) the old maths still
// moved "start" when the left edge was grabbed, so dragging the left edge
// moved the right one.
//
// AND THE ANSWER IS ALWAYS IN ORDER. Sliding an inverted span used to
// produce "2026-09-30 → 2026-09-26" — arithmetically faithful and useless:
// the reader is told the new dates are as wrong as the old ones. The span
// keeps its LENGTH and comes back with the start before the due date, so a
// drag leaves the work item better described than it found it.
//
// A drag of no days writes nothing at all, inverted or not — nobody nudging
// a bar four pixels meant to rewrite both its dates.
export function datesAfterDrag({ start, due, handle, days }) {
  const n = Math.round(Number(days) || 0);
  if (!n) return null;
  const a = start ? String(start).slice(0, 10) : '';
  const b = due ? String(due).slice(0, 10) : '';
  if (!a && !b) return null;

  // One date only — a diamond. It has no edges to grab, and the date it has
  // is the one that moves; it stays in the field it was in.
  if (!a || !b) {
    if (handle === 'start' && !a) return null;
    if (handle === 'end' && !b) return null;
    const out = {};
    if (a) out.start = addDays(a, n);
    if (b) out.due = addDays(b, n);
    return changed(out, a, b);
  }

  // The two edges, earliest first — what is actually on screen.
  let lo = a <= b ? a : b;
  let hi = a <= b ? b : a;
  if (handle === 'move') {
    // The whole span slides and KEEPS ITS LENGTH.
    lo = addDays(lo, n);
    hi = addDays(hi, n);
  } else if (handle === 'start') {
    lo = addDays(lo, n);
    if (lo > hi) lo = hi;          // a bar cannot be dragged inside out
  } else if (handle === 'end') {
    hi = addDays(hi, n);
    if (hi < lo) hi = lo;
  } else {
    return null;
  }
  return changed({ start: lo, due: hi }, a, b);
}

// Only the dates that actually moved — a patch that writes a field back to
// the value it already held is a write for nothing, and on this page a write
// costs a request and a spinner on the row.
function changed(patch, a, b) {
  const out = {};
  if (patch.start && patch.start !== a) out.start = patch.start;
  if (patch.due && patch.due !== b) out.due = patch.due;
  return Object.keys(out).length ? out : null;
}

// ---- finding a bar that is not on screen -------------------------------------
// The chart scrolls sideways and the rows do not, so a reader can be looking
// at a row whose bar is two thousand pixels away. The row is right there;
// the work it describes is nowhere. Saying which WAY it is, and offering to
// go, is the whole of it.
//
// `viewW` is the chart's VISIBLE width — the grid's box less the frozen
// column pane over its left-hand edge — and `scrollLeft` doubles as the
// chart-local x of that view's left edge, because the pane is pinned there.
//
//   -1  the bar is off to the left, behind where the reader is looking
//    1  the bar is off to the right, ahead of it
//    0  at least some of it is on screen
export function offView(bar, { scrollLeft = 0, viewW = 0 } = {}) {
  if (!bar || !(viewW > 0)) return 0;
  const l = Number(scrollLeft) || 0;
  if (bar.x + bar.w < l) return -1;
  if (bar.x > l + viewW) return 1;
  return 0;
}

// Where to scroll so the bar is on screen AND has context: its start sits a
// quarter of the way in, not flush against the frozen columns where it reads
// as clipped. Clamped to the chart, so the last bar does not scroll the
// chart off its own end.
export function scrollTargetFor(bar, { viewW = 0, chartW = 0, lead = 0.25 } = {}) {
  if (!bar) return 0;
  const max = Math.max(0, (Number(chartW) || 0) - (Number(viewW) || 0));
  return Math.round(Math.max(0, Math.min(max, bar.x - (Number(viewW) || 0) * lead)));
}
